// Menu → Costing → "Catering menu & recipes" (kitchen flow, go-live): many
// dishes with their recipes from one CSV. Download the template → choose the
// filled file → the server checks every row (dry run) → problem dishes in red
// with the reason (unknown ingredients named, never guessed) → one button
// imports all of it, or nothing. Shown only with the kitchen flow on, for
// admin. Strings: lib/inventoryFlow/menuImportStrings.ts.
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Download, FileUp, Loader2 } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'
import { toastHelpers } from '@/lib/toast-helpers'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { MI } from '@/lib/inventoryFlow/menuImportStrings'
import { canImportMenu, dishResultLabel, perServingTyped, sortDishes } from '@/lib/inventoryFlow/menuImportLogic'
import type { MenuImportResult } from '@/lib/inventoryFlow/menuImportTypes'

const MAX_BYTES = 2 * 1024 * 1024

export function MenuImportCard() {
  const on = useKitchenFlowEnabled()
  const [open, setOpen] = useState(false)
  if (!on || getStoredUserRole() !== 'admin') return null
  return (
    <Card>
      <CardContent className="space-y-3 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium">{MI.title}</p>
            <p className="text-sm text-muted-foreground">{MI.help}</p>
          </div>
          <Button variant="outline" onClick={() => setOpen((o) => !o)}>{open ? MI.close : MI.open}</Button>
        </div>
        {open && <MenuImportForm onDone={() => setOpen(false)} />}
      </CardContent>
    </Card>
  )
}

function MenuImportForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<MenuImportResult | null>(null)
  const [busy, setBusy] = useState<'check' | 'import' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [key, setKey] = useState(newIdempotencyKey)
  const msg = (e: unknown) => (e instanceof ApiClientError ? e.message.split(' — ')[0] : (e as Error).message)

  const check = async (f: File) => {
    setBusy('check')
    setError(null)
    setResult(null)
    try {
      const r = await apiClient.importMenuRecipes(f, true)
      setResult(r.data ?? null)
    } catch (e) {
      setError(msg(e))
    } finally {
      setBusy(null)
    }
  }

  const doImport = async () => {
    if (!file) return
    setBusy('import')
    setError(null)
    try {
      const r = await apiClient.importMenuRecipes(file, false, key)
      for (const k of ['admin-products', 'products', 'reports-overview', 'categories', 'admin-categories']) qc.invalidateQueries({ queryKey: [k] })
      toastHelpers.success(MI.title, MI.done(r.data?.created ?? 0, r.data?.updated ?? 0))
      onDone()
    } catch (e) {
      // Someone changed the stock items since the check: show the fresh one.
      if (e instanceof ApiClientError && e.code === 'menu_import_has_errors' && e.payload) {
        setResult(e.payload as MenuImportResult)
        return
      }
      setError(msg(e))
    } finally {
      setBusy(null)
    }
  }

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    setResult(null)
    setError(null)
    if (!picked) return
    if (picked.size > MAX_BYTES) {
      setError('That file is too big.')
      return
    }
    setFile(picked)
    setKey(newIdempotencyKey())
    void check(picked)
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{MI.step1}</p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline"
          onClick={() => apiClient.downloadMenuImportTemplate().catch((e: Error) => toastHelpers.error('Download failed', e.message))}>
          <Download className="mr-2 h-4 w-4" /> {MI.download}
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onPick} data-testid="menu-import-file" />
        <Button type="button" onClick={() => fileRef.current?.click()} disabled={busy !== null}>
          <FileUp className="mr-2 h-4 w-4" /> {file ? file.name : MI.choose}
        </Button>
      </div>

      {busy === 'check' && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {MI.checking}</p>}
      {error && (
        <p className="flex items-start gap-2 rounded-md bg-danger-surface/50 px-3 py-2 text-sm text-danger-fg">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </p>
      )}

      {result && (
        <div className="space-y-3">
          <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${result.error_dishes > 0 ? 'bg-danger-surface/50 text-danger-fg' : 'bg-success-surface/50 text-success-fg'}`}>
            {result.error_dishes > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {MI.summary(result.valid_dishes, result.error_dishes)}
          </div>
          {result.error_dishes > 0 && <p className="text-sm">{MI.fixFirst}</p>}
          {(result.duplicate_dishes?.length ?? 0) > 0 && <p className="text-sm text-danger-fg">{MI.duplicates(result.duplicate_dishes ?? [])}</p>}
          {result.unknown_ingredients.length > 0 && <p className="text-sm text-danger-fg">{MI.unknown(result.unknown_ingredients)}</p>}
          {result.new_categories.length > 0 && <p className="text-sm text-muted-foreground">{MI.newCategories(result.new_categories)}</p>}
          {result.warnings.map((w) => <p key={w} className="text-sm text-warning-fg">{w}</p>)}

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">{MI.colDish}</th>
                  <th className="px-2 py-2 text-right">{MI.colPrice}</th>
                  <th className="px-2 py-2">{MI.colRecipe}</th>
                  <th className="px-2 py-2">{MI.colResult}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sortDishes(result.dishes).map((d) => {
                  const bad = d.errors.length > 0
                  return (
                    <tr key={d.dish} className={bad ? 'bg-danger-surface/40' : undefined}>
                      <td className="px-2 py-2 align-top font-medium">
                        {d.dish}
                        <span className="block text-xs font-normal text-muted-foreground">
                          {d.category}{d.category_is_new ? ' (new)' : ''}{d.makes !== 1 ? ` · ${MI.makes(d.makes)}` : ''}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right align-top tabular-nums">
                        {d.old_price != null && d.price != null && d.old_price !== d.price
                          ? MI.priceChange(formatCurrency(d.old_price), MI.perServing(formatCurrency(d.price)))
                          : d.price != null ? MI.perServing(formatCurrency(d.price)) : d.old_price != null ? MI.perServing(formatCurrency(d.old_price)) : '—'}
                      </td>
                      <td className="px-2 py-2 align-top text-xs">
                        {d.lines.map((l) => (
                          <span key={l.line} className={`block ${l.error ? 'text-danger-fg' : ''}`}>
                            {perServingTyped(l.qty, l.unit, d.makes)} {l.stock_item_name ?? l.ingredient}
                            {l.matched_via === 'merge' && <span className="text-muted-foreground"> ({MI.viaMerge}: {l.ingredient})</span>}
                            {MI.scope[l.order_type_scope] && <span className="text-muted-foreground"> · {MI.scope[l.order_type_scope]}</span>}
                          </span>
                        ))}
                      </td>
                      <td className={`px-2 py-2 align-top text-xs ${bad ? 'font-medium text-danger-fg' : 'text-success-fg'}`}>
                        {bad ? d.errors.join('; ') : dishResultLabel(d)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <Button type="button" className="w-full" disabled={!canImportMenu(result) || busy !== null} onClick={() => void doImport()}>
            {busy === 'import' ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {MI.importing}</> : MI.confirm(result.valid_dishes)}
          </Button>
        </div>
      )}
    </div>
  )
}
